<template>
  <HintBox v-if="isVisibleHintBox" :ref-target="refButtonLanguage" :message-hint-box="t('detected_language_hintbox')" />

  <ButtonTemplateMicInput
    class="lang-grd-mic"
    :set-ref-button="(el) => (refButtonLanguage = el)"
    :is-open="isVisiblePopupLang"
    :text-button="t('language')"
    @click="toggleLanguagePopup"
  >
    <template #icon-handler>
      <h4
        class="accent"
        :class="{
          'long-language': ['id', 'ms'].includes(audioStore.languageSelected?.code),
        }"
      >
        <template v-if="audioStore.languageSelected?.code === 'id'"> Bahasa<br />Indonesia </template>
        <template v-else-if="audioStore.languageSelected?.code === 'ms'"> Bahasa<br />Melayu </template>
        <template v-else>
          {{ audioStore.languageSelected?.name }}
        </template>
      </h4>
    </template>
  </ButtonTemplateMicInput>

  <Teleport to="body">
    <DropdownBottom v-if="isVisiblePopupLang" :target-el="refButtonLanguage" @close="toggleLanguagePopup">
      <LanguageList
        :languages="languages"
        :is-using-language-detection="isUsingLanguageDetection"
        :reverse-order="true"
        :show-all-in-list="true"
        @select="handleSelectLanguage"
        @close="toggleLanguagePopup"
      />
    </DropdownBottom>
  </Teleport>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';

import ButtonTemplateMicInput from '@/components/domains/chat/common/ButtonTemplateMicInput.vue';
import DropdownBottom from '@/components/shared/base/DropdownBottom.vue';
import LanguageList from '@/components/shared/base/LanguageList.vue';
import HintBox from '@/components/shared/composite/HintBox.vue';
import { debugLog } from '@/static/develop.js';
import { useAudioStore } from '@/stores/audio';
import { useLanguageStore } from '@/stores/language';
import { useMicrophoneStore } from '@/stores/microphone';
import { useSocketStore } from '@/stores/socket';
import { useToastStore } from '@/stores/toast.js';
import { Language, TypeCall } from '@/types/common';
import { ij_currentPopupMicInput, ij_setCurrentPopupMicInput } from '@/utils/injectionKeys.js';

const props = withDefaults(
  defineProps<{
    typeCall?: TypeCall;
  }>(),
  {
    typeCall: 'GENERAL',
  }
);

const { t } = useI18n();

const audioStore = useAudioStore();
const toastStore = useToastStore();
const socketStore = useSocketStore();
const microphoneStore = useMicrophoneStore();
const languageStore = useLanguageStore();

const { showHintBox } = storeToRefs(toastStore);
const { languageDetected, languageSelected } = storeToRefs(audioStore);

const currentPopupMicInput = inject(ij_currentPopupMicInput, ref(''));
const setCurrentPopupMicInput = inject(ij_setCurrentPopupMicInput, (popupName: string) => {});

const refButtonLanguage = ref<HTMLElement>();
const showHintLanguage = ref(false);

const isUsingLanguageDetection = computed<boolean>(() => {
  return ['GENERAL', 'CONFERENCE'].includes(props.typeCall);
});

const languages = computed<Language[]>(() => {
  return ['GENERAL', 'CONFERENCE'].includes(props.typeCall) && microphoneStore.isMicOn
    ? languageStore.interpretationLanguageOptions.inputEnabled
    : languageStore.interpretationLanguageOptions.textOutputEnabled;
});
// 다른 언어가 감지되었습니다 힌트박스 노출 여부
const isVisibleHintBox = computed(() => {
  return showHintBox.value === 'language' && isUsingLanguageDetection.value;
});

// 언어 선택 드랍다운 보임 여부
const isVisiblePopupLang = computed((): boolean => {
  return currentPopupMicInput.value === 'language';
});

const toggleLanguagePopup = () => {
  showHintLanguage.value = false;
  languageDetected.value = null;

  setCurrentPopupMicInput('language');
};

// 언어 선택 시
const handleSelectLanguage = (language) => {
  if (showHintLanguage.value) {
    showHintLanguage.value = false;
  }

  setCurrentPopupMicInput('');

  const previousLanguage = audioStore.languageSelected;

  audioStore.selectLanguage(language.code);

  if (language.code === languageDetected.value?.languageCode) {
    languageDetected.value = null;
  }

  const result = socketStore.sendMessageChangeLanguage(language.code);
  if (!result) {
    debugLog('Failed to change language');

    audioStore.selectLanguage(previousLanguage.code);
    toastStore.error(t('toast_failed_to_change_language'), 'LANGUAGE');
    return;
  }

  toastStore.info(language.name, 'LANGUAGE');
};

// 감지된 언어와 선택된 언어가 다를 경우
watch(
  () => languageDetected.value,
  async (newVal) => {
    if (!newVal || !isUsingLanguageDetection.value) return;
    if (languageSelected.value.code !== newVal.languageCode) {
      switch (newVal.step) {
        case 1:
          showHintBox.value = 'language';
          setTimeout(() => {
            if (isVisibleHintBox.value) showHintBox.value = '';
          }, 5000);
          break;
        case 2:
          if (currentPopupMicInput.value !== 'language') setCurrentPopupMicInput('language');
          break;
      }
    }
  }
);
</script>

<style scoped lang="scss">
:root[data-theme='light'] {
  .lang-grd-mic {
    :deep(.button-mic)[data-off='false'] {
      background-color: $primary-bg;
      box-shadow: none;
      h4 {
        background: var(--font-gradient-language);
        -webkit-text-fill-color: transparent;
        background-clip: text;
        -webkit-background-clip: text;
      }
    }
  }
}

.language-gradient {
  background: var(--font-gradient-language);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;
}

::v-deep(.icon-handler) {
  width: fit-content;
  min-width: fit-content;
  max-width: fit-content;
}

.long-language {
  font-size: $font-size-psmall;
  line-height: 1;
  text-align: center;
  white-space: normal;
}

::v-deep(.dropdown-content) {
  top: unset !important;
  // bottom: env(safe-area-inset-bottom, 8.5rem);
  bottom: 88px;
  width: 20rem;
  min-width: unset;
}
h4.accent {
  font-size: 1rem;
  padding-right: 1px;
  padding-bottom: 1px;
}

.scroll-hidden {
  :deep(.dropdown-content) {
    overflow-y: hidden !important;
  }
}
</style>
